/**
=========================================================
* Trust Cargo - public site sections
=========================================================

* Stats, services, why-us, how-we-work, news, the closing call to
* action and the contact block.
*
* Two of these are wired to live data rather than written by hand:
*   Stats - counts the real containers and customers from the API, so the
*           numbers on the public site can never drift from the truth.
*   News  - renders the Posts the admin already manages, so publishing an
*           offer on the dashboard puts it on the website.
*
* Lists use the <Reveal> wrapper, never the useReveal hook inline: the news
* list grows from empty to full when the fetch lands, and a hook called in a
* .map() callback would change the hook count between renders.
*/

import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Icon from "@mui/material/Icon";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import { C, Section, Heading, LineArt, Reveal, useReveal, goldButton } from "./ui";
import { COMPANY } from "./content";

const API = "https://trust-cargo.co/anas_backend/api";

/** Post bodies are editor HTML; the cards show the text. */
const plain = (html) =>
  String(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/* ------------------------------------------------------------------ */
/* Stats                                                               */
/* ------------------------------------------------------------------ */

/** Counts up to the figure once the strip scrolls into view. */
function Counter({ to, suffix }) {
  const [n, setN] = useState(0);
  const [ref, style] = useReveal();

  useEffect(() => {
    if (style.opacity !== 1 || !to) return undefined;

    const started = performance.now();
    const dur = 1400;
    let raf;

    const tick = (now) => {
      const p = Math.min(1, (now - started) / dur);
      // ease-out so it decelerates into the final number
      setN(Math.round(to * (1 - (1 - p) ** 3)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [style.opacity, to]);

  return (
    <SuiTypography
      ref={ref}
      sx={{ fontSize: { xs: 32, md: 44 }, fontWeight: 800, color: C.goldLight, lineHeight: 1.1 }}
    >
      {`${n.toLocaleString("en-US")}${suffix || ""}`}
    </SuiTypography>
  );
}

Counter.defaultProps = { suffix: "" };
Counter.propTypes = { to: PropTypes.number.isRequired, suffix: PropTypes.string };

export function Stats({ t }) {
  const [counts, setCounts] = useState({ containers: 0, clients: 0 });

  useEffect(() => {
    let alive = true;

    // real figures, so nothing on the public page is invented
    Promise.all([
      fetch(`${API}/containers`).then((r) => (r.ok ? r.json() : null)),
      fetch(`${API}/customers`).then((r) => (r.ok ? r.json() : null)),
    ])
      .then(([c, k]) => {
        if (!alive) return;
        setCounts({
          containers: (c?.containers || []).length,
          clients: (k?.customers || []).length,
        });
      })
      .catch(() => {});

    return () => {
      alive = false;
    };
  }, []);

  const items = [
    { value: counts.containers, label: t.stats.containers },
    { value: counts.clients, label: t.stats.clients },
    { value: 6, label: t.stats.services },
    { value: 24, label: t.stats.support, suffix: "/7" },
  ];

  return (
    <SuiBox
      sx={{
        position: "relative",
        py: { xs: 5, md: 6 },
        px: { xs: 2.5, md: 5 },
        backgroundColor: C.navyDeep,
        borderTop: `1px solid ${C.line}`,
        borderBottom: `1px solid ${C.line}`,
      }}
    >
      <LineArt opacity={0.7} />

      <SuiBox
        sx={{
          position: "relative",
          zIndex: 2,
          maxWidth: 1220,
          mx: "auto",
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
          gap: { xs: 3, md: 2 },
          textAlign: "center",
        }}
      >
        {items.map((s) => (
          <SuiBox key={s.label}>
            <Counter to={s.value} suffix={s.suffix} />
            <SuiTypography sx={{ fontSize: 14, color: C.muted, mt: 0.5, fontWeight: 600 }}>
              {s.label}
            </SuiTypography>
          </SuiBox>
        ))}
      </SuiBox>

      <SuiTypography
        sx={{
          position: "relative",
          zIndex: 2,
          textAlign: "center",
          fontSize: 12,
          color: "rgba(247,244,238,0.4)",
          mt: 3,
        }}
      >
        {t.statsNote}
      </SuiTypography>
    </SuiBox>
  );
}

/* eslint-disable react/forbid-prop-types */
Stats.propTypes = { t: PropTypes.object.isRequired };

/* ------------------------------------------------------------------ */
/* Services                                                            */
/* ------------------------------------------------------------------ */

export function Services({ t }) {
  return (
    <Section id="services">
      <Heading title={t.servicesTitle} lead={t.servicesLead} center />

      <SuiBox
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(3, 1fr)" },
          gap: 3,
        }}
      >
        {t.services.map((s, n) => (
          <Reveal key={s.title} delay={n * 70}>
            <SuiBox
              sx={{
                height: "100%",
                position: "relative",
                p: { xs: 3, md: 3.5 },
                borderRadius: 4,
                backgroundColor: C.white,
                border: "1px solid rgba(11,27,58,0.07)",
                boxShadow: "0 10px 34px rgba(11,27,58,0.07)",
                overflow: "hidden",
                transition: "transform 260ms ease, box-shadow 260ms ease",
                // a gold bar grows along the top edge on hover
                "&:before": {
                  content: '""',
                  position: "absolute",
                  insetInline: 0,
                  top: 0,
                  height: 4,
                  background: `linear-gradient(90deg, ${C.gold}, ${C.goldLight})`,
                  transform: "scaleX(0)",
                  transformOrigin: "left",
                  transition: "transform 320ms ease",
                },
                "&:hover": {
                  transform: "translateY(-6px)",
                  boxShadow: "0 22px 50px rgba(11,27,58,0.14)",
                },
                "&:hover:before": { transform: "scaleX(1)" },
              }}
            >
              <SuiBox
                sx={{
                  width: 58,
                  height: 58,
                  borderRadius: 3,
                  display: "grid",
                  placeItems: "center",
                  mb: 2.5,
                  background:
                    "linear-gradient(135deg, rgba(201,162,39,0.16), rgba(201,162,39,0.05))",
                  border: `1px solid ${C.line}`,
                }}
              >
                <Icon sx={{ fontSize: "30px !important", color: C.gold }}>{s.icon}</Icon>
              </SuiBox>

              <SuiTypography sx={{ fontSize: 20, fontWeight: 800, color: C.navy, mb: 1 }}>
                {s.title}
              </SuiTypography>
              <SuiTypography sx={{ fontSize: 15, lineHeight: 1.95, color: "rgba(11,27,58,0.68)" }}>
                {s.text}
              </SuiTypography>
            </SuiBox>
          </Reveal>
        ))}
      </SuiBox>
    </Section>
  );
}

Services.propTypes = { t: PropTypes.object.isRequired };

/* ------------------------------------------------------------------ */
/* Why us                                                              */
/* ------------------------------------------------------------------ */

export function Why({ t }) {
  return (
    <SuiBox
      sx={{
        position: "relative",
        py: { xs: 7, md: 11 },
        px: { xs: 2.5, md: 5 },
        backgroundColor: C.navy,
        overflow: "hidden",
      }}
    >
      <LineArt />

      <SuiBox sx={{ position: "relative", zIndex: 2, maxWidth: 1220, mx: "auto" }}>
        <Heading title={t.whyTitle} dark center />

        <SuiBox
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" },
            gap: 2.5,
          }}
        >
          {t.why.map((w, n) => (
            <Reveal key={w.title} delay={n * 80}>
              <SuiBox
                sx={{
                  height: "100%",
                  p: 3,
                  borderRadius: 4,
                  background: "rgba(255,255,255,0.04)",
                  border: `1px solid ${C.line}`,
                  transition: "background-color 240ms ease",
                  "&:hover": { background: "rgba(201,162,39,0.10)" },
                }}
              >
                <Icon sx={{ fontSize: "34px !important", color: C.goldLight, mb: 1.5 }}>
                  {w.icon}
                </Icon>
                <SuiTypography sx={{ fontSize: 18, fontWeight: 800, color: C.white, mb: 1 }}>
                  {w.title}
                </SuiTypography>
                <SuiTypography sx={{ fontSize: 14.5, lineHeight: 1.9, color: C.muted }}>
                  {w.text}
                </SuiTypography>
              </SuiBox>
            </Reveal>
          ))}
        </SuiBox>
      </SuiBox>
    </SuiBox>
  );
}

Why.propTypes = { t: PropTypes.object.isRequired };

/* ------------------------------------------------------------------ */
/* How we work                                                         */
/* ------------------------------------------------------------------ */

export function Process({ t }) {
  return (
    <Section id="process" tint={C.white}>
      <Heading title={t.processTitle} lead={t.processLead} center />

      <SuiBox
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(3, 1fr)" },
          gap: 3,
        }}
      >
        {t.process.map((p, n) => (
          <Reveal key={p.title} delay={n * 70}>
            <SuiBox
              sx={{
                height: "100%",
                position: "relative",
                p: 3.5,
                borderRadius: 4,
                backgroundColor: C.ivory,
                border: "1px solid rgba(11,27,58,0.06)",
                overflow: "hidden",
              }}
            >
              {/* the step number, big and faint behind the text */}
              <SuiTypography
                sx={{
                  position: "absolute",
                  insetInlineEnd: 18,
                  top: 6,
                  fontSize: 64,
                  fontWeight: 800,
                  lineHeight: 1,
                  color: "rgba(201,162,39,0.16)",
                }}
              >
                {n + 1}
              </SuiTypography>

              <SuiBox sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.5 }}>
                <SuiBox
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    flexShrink: 0,
                    background: `linear-gradient(135deg, ${C.goldLight}, ${C.gold})`,
                    color: C.navy,
                    fontWeight: 800,
                    fontSize: 15,
                  }}
                >
                  {n + 1}
                </SuiBox>
                <SuiTypography sx={{ fontSize: 19, fontWeight: 800, color: C.navy }}>
                  {p.title}
                </SuiTypography>
              </SuiBox>

              <SuiTypography sx={{ fontSize: 15, lineHeight: 1.95, color: "rgba(11,27,58,0.68)" }}>
                {p.text}
              </SuiTypography>
            </SuiBox>
          </Reveal>
        ))}
      </SuiBox>
    </Section>
  );
}

Process.propTypes = { t: PropTypes.object.isRequired };

/* ------------------------------------------------------------------ */
/* News - the Posts the admin publishes                                */
/* ------------------------------------------------------------------ */

export function News({ t }) {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    let alive = true;

    fetch(`${API}/posts`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (alive) setPosts((d?.posts || []).slice(0, 6));
      })
      .catch(() => {});

    return () => {
      alive = false;
    };
  }, []);

  return (
    <Section id="news" tint={C.white}>
      <Heading title={t.newsTitle} lead={t.newsLead} center />

      {posts.length === 0 ? (
        <SuiTypography sx={{ textAlign: "center", color: "rgba(11,27,58,0.5)", py: 4 }}>
          {t.newsEmpty}
        </SuiTypography>
      ) : (
        <SuiBox
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(3, 1fr)" },
            gap: 3,
          }}
        >
          {posts.map((p, n) => {
            const cover = p.images && p.images.length ? p.images[0].image : null;

            return (
              <Reveal key={p.id} delay={n * 70}>
                <SuiBox
                  sx={{
                    height: "100%",
                    borderRadius: 4,
                    overflow: "hidden",
                    backgroundColor: C.ivory,
                    border: "1px solid rgba(11,27,58,0.07)",
                    transition: "box-shadow 260ms ease, transform 260ms ease",
                    "&:hover": {
                      transform: "translateY(-6px)",
                      boxShadow: "0 22px 50px rgba(11,27,58,0.14)",
                    },
                  }}
                >
                  <SuiBox
                    sx={{
                      height: 190,
                      background: cover
                        ? `linear-gradient(180deg, rgba(11,27,58,0) 55%, rgba(11,27,58,0.55) 100%), url(${cover}) center/cover`
                        : `linear-gradient(135deg, ${C.navy}, ${C.navySoft})`,
                      display: "grid",
                      placeItems: "center",
                    }}
                  >
                    {!cover && (
                      <Icon sx={{ fontSize: "46px !important", color: C.gold }}>campaign</Icon>
                    )}
                  </SuiBox>

                  <SuiBox sx={{ p: 3 }}>
                    <SuiTypography
                      sx={{
                        fontSize: 15.5,
                        lineHeight: 1.95,
                        color: "rgba(11,27,58,0.78)",
                        display: "-webkit-box",
                        WebkitLineClamp: 4,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {plain(p.body) || "-"}
                    </SuiTypography>
                  </SuiBox>
                </SuiBox>
              </Reveal>
            );
          })}
        </SuiBox>
      )}
    </Section>
  );
}

News.propTypes = { t: PropTypes.object.isRequired };

/* ------------------------------------------------------------------ */
/* Closing call to action                                              */
/* ------------------------------------------------------------------ */

export function Cta({ t }) {
  return (
    <SuiBox sx={{ px: { xs: 2.5, md: 5 }, py: { xs: 5, md: 8 }, backgroundColor: C.ivory }}>
      <Reveal>
        <SuiBox
          sx={{
            position: "relative",
            maxWidth: 1220,
            mx: "auto",
            borderRadius: 5,
            overflow: "hidden",
            px: { xs: 3, md: 8 },
            py: { xs: 5, md: 8 },
            textAlign: "center",
            background: `
              radial-gradient(700px 300px at 20% 0%, rgba(201,162,39,0.25), transparent 60%),
              linear-gradient(140deg, ${C.navy}, ${C.navyDeep})
            `,
            border: `1px solid ${C.line}`,
          }}
        >
          <SuiTypography
            sx={{ fontSize: { xs: 26, md: 38 }, fontWeight: 800, color: C.white, mb: 1.5 }}
          >
            {t.ctaTitle}
          </SuiTypography>
          <SuiTypography
            sx={{
              fontSize: { xs: 15, md: 17 },
              color: C.muted,
              mb: 4,
              maxWidth: 620,
              mx: "auto",
              lineHeight: 1.95,
            }}
          >
            {t.ctaText}
          </SuiTypography>

          <SuiBox
            component="a"
            href={`https://wa.me/${COMPANY.whatsapp}`}
            target="_blank"
            rel="noreferrer"
            sx={{ ...goldButton, textDecoration: "none" }}
          >
            <Icon sx={{ fontSize: "20px !important" }}>chat</Icon>
            {t.ctaButton}
          </SuiBox>
        </SuiBox>
      </Reveal>
    </SuiBox>
  );
}

Cta.propTypes = { t: PropTypes.object.isRequired };

/* ------------------------------------------------------------------ */
/* Contact                                                             */
/* ------------------------------------------------------------------ */

export function Contact({ t, lang }) {
  const [name, setName] = useState("");
  const [msg, setMsg] = useState("");

  /**
   * No fake form. The details are composed into a WhatsApp message and the
   * app opens with it ready to send - which actually reaches someone, unlike
   * a POST to an endpoint that does not exist.
   */
  const send = () => {
    const text = encodeURIComponent(`${name ? `${name}: ` : ""}${msg}`);
    window.open(`https://wa.me/${COMPANY.whatsapp}?text=${text}`, "_blank", "noopener");
  };

  const cards = [
    {
      icon: "call",
      label: t.contact.phone,
      value: COMPANY.phoneDisplay,
      href: `tel:${COMPANY.phone}`,
    },
    { icon: "mail", label: t.contact.email, value: COMPANY.email, href: `mailto:${COMPANY.email}` },
    {
      icon: "place",
      label: t.contact.address,
      value: lang === "ar" ? COMPANY.addressAr : COMPANY.addressEn,
    },
    {
      icon: "schedule",
      label: t.contact.hours,
      value: lang === "ar" ? COMPANY.hoursAr : COMPANY.hoursEn,
    },
  ];

  const field = {
    width: "100%",
    fontFamily: "inherit",
    fontSize: 15,
    color: C.white,
    background: "rgba(255,255,255,0.05)",
    border: `1px solid ${C.line}`,
    borderRadius: 3,
    padding: "14px 16px",
    outline: "none",
    "&:focus": { borderColor: C.gold, background: "rgba(255,255,255,0.09)" },
    "&::placeholder": { color: "rgba(247,244,238,0.45)" },
  };

  return (
    <SuiBox
      id="contact"
      sx={{
        position: "relative",
        py: { xs: 7, md: 11 },
        px: { xs: 2.5, md: 5 },
        backgroundColor: C.navy,
        overflow: "hidden",
      }}
    >
      <LineArt />

      <SuiBox sx={{ position: "relative", zIndex: 2, maxWidth: 1220, mx: "auto" }}>
        <Heading title={t.contactTitle} lead={t.contactLead} dark center />

        <SuiBox
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: { xs: 3, md: 5 },
            alignItems: "start",
          }}
        >
          <SuiBox sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            {cards.map((c) => (
              <SuiBox
                key={c.label}
                component={c.href ? "a" : "div"}
                href={c.href}
                sx={{
                  p: 2.5,
                  borderRadius: 3.5,
                  textDecoration: "none",
                  display: "block",
                  background: "rgba(255,255,255,0.04)",
                  border: `1px solid ${C.line}`,
                  transition: "background-color 220ms ease",
                  "&:hover": { background: "rgba(201,162,39,0.12)" },
                }}
              >
                <Icon sx={{ fontSize: "26px !important", color: C.goldLight, mb: 1 }}>
                  {c.icon}
                </Icon>
                <SuiTypography sx={{ fontSize: 12.5, color: C.muted, mb: 0.5 }}>
                  {c.label}
                </SuiTypography>
                <SuiTypography
                  sx={{ fontSize: 15, fontWeight: 700, color: C.white, wordBreak: "break-word" }}
                >
                  {c.value}
                </SuiTypography>
              </SuiBox>
            ))}
          </SuiBox>

          <SuiBox sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <SuiBox
              component="input"
              sx={field}
              placeholder={t.contact.name}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <SuiBox
              component="textarea"
              rows={5}
              sx={{ ...field, resize: "vertical" }}
              placeholder={t.contact.message}
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
            />

            <SuiBox
              component="button"
              type="button"
              onClick={send}
              disabled={!msg.trim()}
              sx={{
                ...goldButton,
                opacity: msg.trim() ? 1 : 0.45,
                cursor: msg.trim() ? "pointer" : "not-allowed",
              }}
            >
              <Icon sx={{ fontSize: "20px !important" }}>send</Icon>
              {t.contact.send}
            </SuiBox>

            <SuiTypography sx={{ fontSize: 12.5, color: C.muted, textAlign: "center" }}>
              {t.contact.hint}
            </SuiTypography>
          </SuiBox>
        </SuiBox>
      </SuiBox>
    </SuiBox>
  );
}

Contact.propTypes = {
  t: PropTypes.object.isRequired,
  lang: PropTypes.string.isRequired,
};

/* ------------------------------------------------------------------ */
/* Footer                                                              */
/* ------------------------------------------------------------------ */

export function Footer({ t, sections, onGo }) {
  return (
    <SuiBox
      component="footer"
      sx={{ backgroundColor: C.navyDeep, px: { xs: 2.5, md: 5 }, pt: { xs: 6, md: 8 }, pb: 3 }}
    >
      <SuiBox
        sx={{
          maxWidth: 1220,
          mx: "auto",
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "2fr 1fr 1.4fr" },
          gap: { xs: 4, md: 6 },
        }}
      >
        <SuiBox>
          <SuiBox sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
            <SuiBox
              sx={{
                width: 42,
                height: 42,
                borderRadius: 2.5,
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
                background: `linear-gradient(135deg, ${C.goldLight}, ${C.gold})`,
              }}
            >
              <Icon sx={{ color: C.navy, fontSize: "24px !important" }}>directions_boat</Icon>
            </SuiBox>
            <SuiBox>
              <SuiTypography sx={{ fontSize: 19, fontWeight: 800, color: C.white }}>
                {t.brand}
              </SuiTypography>
              <SuiTypography sx={{ fontSize: 11.5, color: C.gold, fontWeight: 600 }}>
                {t.brandSub}
              </SuiTypography>
            </SuiBox>
          </SuiBox>

          <SuiTypography sx={{ fontSize: 14.5, lineHeight: 2, color: C.muted, maxWidth: 420 }}>
            {t.footerAbout}
          </SuiTypography>

          <SuiBox sx={{ display: "flex", gap: 1.25, mt: 2.5 }}>
            {[
              { icon: "facebook", href: COMPANY.facebook },
              { icon: "photo_camera", href: COMPANY.instagram },
              { icon: "chat", href: `https://wa.me/${COMPANY.whatsapp}` },
            ].map((s) => (
              <SuiBox
                key={s.icon}
                component="a"
                href={s.href}
                target="_blank"
                rel="noreferrer"
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  color: C.ivory,
                  background: "rgba(255,255,255,0.06)",
                  border: `1px solid ${C.line}`,
                  "&:hover": { background: C.gold, color: C.navy },
                }}
              >
                <Icon>{s.icon}</Icon>
              </SuiBox>
            ))}
          </SuiBox>
        </SuiBox>

        <SuiBox>
          <SuiTypography sx={{ fontSize: 16, fontWeight: 800, color: C.white, mb: 2 }}>
            {t.footerLinks}
          </SuiTypography>
          {sections.map((s) => (
            <SuiTypography
              key={s.id}
              onClick={() => onGo(s.id)}
              sx={{
                cursor: "pointer",
                fontSize: 14.5,
                color: C.muted,
                py: 0.6,
                "&:hover": { color: C.goldLight },
              }}
            >
              {s.label}
            </SuiTypography>
          ))}
        </SuiBox>

        <SuiBox>
          <SuiTypography sx={{ fontSize: 16, fontWeight: 800, color: C.white, mb: 2 }}>
            {t.footerContact}
          </SuiTypography>

          {[
            { icon: "call", value: COMPANY.phoneDisplay, href: `tel:${COMPANY.phone}` },
            { icon: "mail", value: COMPANY.email, href: `mailto:${COMPANY.email}` },
            { icon: "place", value: t.dir === "rtl" ? COMPANY.addressAr : COMPANY.addressEn },
          ].map((c) => (
            <SuiBox
              key={c.icon}
              component={c.href ? "a" : "div"}
              href={c.href}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                py: 0.7,
                textDecoration: "none",
                color: C.muted,
                "&:hover": { color: C.goldLight },
              }}
            >
              <Icon sx={{ fontSize: "18px !important", color: C.gold }}>{c.icon}</Icon>
              <SuiTypography sx={{ fontSize: 14.5, color: "inherit" }}>{c.value}</SuiTypography>
            </SuiBox>
          ))}
        </SuiBox>
      </SuiBox>

      <SuiBox
        sx={{
          maxWidth: 1220,
          mx: "auto",
          mt: 5,
          pt: 2.5,
          borderTop: "1px solid rgba(255,255,255,0.08)",
          textAlign: "center",
        }}
      >
        <SuiTypography sx={{ fontSize: 13, color: "rgba(247,244,238,0.45)" }}>
          {`© ${new Date().getFullYear()} ${t.brand} - ${t.rights}`}
        </SuiTypography>
      </SuiBox>
    </SuiBox>
  );
}

Footer.propTypes = {
  t: PropTypes.object.isRequired,
  sections: PropTypes.arrayOf(PropTypes.object).isRequired,
  onGo: PropTypes.func.isRequired,
};
